<template>
<Form
	v-if="formValues != null"
	@submit="onSubmit"
	@invalid-submit="console.error"
	:validation-schema="schema"
	:initial-values="formValues"
	v-slot="{ errors, meta: meta, }"
>
	<div class="row g-2 p-2">
		<legend class="col-12">Управління звітом</legend>

		<div class="form-group col my-1">
			<label>Початок</label>
			<Field
				name="from"
				type="date"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors.from }"
				:disabled="!isFormEnabled"
			/>
			<div class="invalid-feedback">{{errors.from}}</div>
		</div>

		<div class="form-group col my-1">
			<label>Кінець</label>
			<Field
				name="to"
				type="date"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors.to }"
				:disabled="!isFormEnabled"
			/>
			<div class="invalid-feedback">{{errors.to}}</div>
		</div>

		<div class="form-group col-12 my-1 mb-0">
			<Field
				name="isChart"
				type="checkbox"
				id="is-chart"
				class="form-check-input col-auto"
				:class="{ 'is-invalid': errors.isChart }"
				:value="true"
				:uncheckedValue="false"
				:disabled="!isFormEnabled"
			/>
			<label for="is-chart" class="form-check-label mx-2 my-0 col-auto">
				Додати графік
			</label>
			<div class="invalid-feedback">{{errors.isChart}}</div>
		</div>

		<div class="form-group col-12 my-1">
			<label>Примітки</label>
			<Field
				name="comment"
				as="textarea"
				type="text"
				class="form-control form-control-sm h-60 overflow-auto"
				:class="{ 'is-invalid': errors.comment }"
				:disabled="!isFormEnabled"
			/>
			<div class="invalid-feedback">{{errors.comment}}</div>
		</div>

		<!-- <div class="row justify-content-end my-1 mt-3"> -->
		<div class="form-group col-sm-12 col-md-6 mt-3 mb-1">
			<span class="form-text fst-italic">
				<p 
					class="fs-5 my-0"
					:class="{'text-success': executionOk, 'text-danger': !executionOk}"
				>
					{{ executionResultMessage.length > 0 ? executionResultMessage : "&nbsp;" }}
				</p>
			</span>
		</div>
		<div class="form-group d-flex justify-content-end col-sm-12 col-md-6 mt-3 mb-1">
			<div class="">
				<button
					type="submit"
					class="btn btn-primary btn-sm"
					:disabled="!meta.valid || !isFormEnabled || blockFormTimeout !== null"
				>
					{{ 'Створити звіт' }}
				</button>
			</div>
		</div>
	</div>
</Form>
</template>


<script>
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsStore } from '@/stores/StationsStore.js'
import {
	rpcTaskSchema,
	useRpcTasks,
} from '@/composables/rpcTasks.js'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field } from 'vee-validate';
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup';

export default {
	autocleanPromtTimeout: 5000,
	afterSubmitBlockTimeout: 3000,

	wrongInputPromt: 'Не відповідає вимогам',
	overMinPromt: 'Значення меньше мінімального',
	overMaxPromt: 'Значення більше максимального',

	components: {
		Form,
        Field,
	},

	props: {
		userName: {
			type: String,
			required: true,
		},
		stationId: {
			type: String,
			required: true,
		},
	},

	setup() {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const {
			setFilters,
			createRpc,
			refetch,
		} = useRpcTasks()

		return {
			accountStore,
			stationsStore,

			setFilters,
			createRpc,
			rpcRefetch: refetch,
		}
	},

	data() {
		const schema = Yup.object().shape({
			from: Yup.date()
				.nullable()
				.typeError(this.$options.wrongInputPromt)
				.min(new Date(1900, 0, 1)),
			to: Yup.date()
				.nullable()
				.typeError(this.$options.wrongInputPromt)
				.min(
					Yup.ref('from'),
					"Кінець не може бути раніше початку"
                ),
			isChart: Yup.boolean()
				.typeError(this.$options.wrongInputPromt)
				.required()
				.default(false),
			// fileType: Yup.string()
			// 	.typeError(this.$options.wrongInputPromt),
			comment: Yup.string()
				.typeError(this.$options.wrongInputPromt)
				.transform((value) => (value === '' ? null : value))
				.nullable()
				.min(4, this.$options.overMinLen)
				.max(60, this.$options.overMaxLen)
				.matches(/^[0-9а-яА-Яa-zA-ZґҐєЄіІїЇ "«».,'()№!/\-///—/\n]*$/,
					this.$options.wrongInputPromt),
		})

		return {
			schema,
			formValues: null,

			//  
			executionResultMessage: '',
			executionOk: null,
			cleanTimeout: null,

			// For blocking form after submit.
			blockFormTimeout: null,
		}
	},

	mounted () {
		// Set form defaults
		this.formValues = {
			from: this.monthFirstDayDate,
			to: this.monthLastDayDate,
			isChart: true,
		}
	},

	methods: {
		// Return formated date YYYY-MM-DD
		formatDate(date) {
			return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
		},

		onCancel() { 
			//console.log("cancel")
		},

		async checkActiveTask() {
			this.setFilters({
				destinationStationIds: this.stationId,
				isComplete: false,
			})

			const result = await this.rpcRefetch()
			if (result.error) {
				return null
			}

			return result.data ?? []
		},

		async onSubmit(values) {
			//console.log("submit ", values)

			this.autoBlockForm()

			// Check station for active RPCs
			const taskInQueue = await this.checkActiveTask()
			if (taskInQueue === null) {
				this.printErr("Сталась помилка. Сервер відхилив запит.")
				return
			} else if (taskInQueue.length > 0) {
				this.printErr("Станція вже обробляє інший запит. Будь ласка дочекайтесь його виконання.")
				return
			} else if (!this.station.isActive) {
				this.printErr('Станція не активна. Тільки активні станції виконують запити.')
				return false
			}
			//console.log("Task in queue =", taskInQueue)
			values[rpcTaskSchema.context.from.key] = new Date(values[rpcTaskSchema.context.from.key])
			values[rpcTaskSchema.context.to.key] = new Date(values[rpcTaskSchema.context.to.key])
			values.writer = {
				"name": this.accountStore.userName,
				"id": this.accountStore.userId,
				"organization": {
					"name": this.accountStore.organization["title"],
					"id": this.accountStore.organization["_id"],
				},
			}
			//values.organization = this.accountStore.organization["title"]

			const result = await this.createRpc(
				this.accountStore.userId,
				rpcTaskSchema.job.allowed.buildStationReport.key,
				this.stationId,
				null,
				values
			)

			if (result) {
				this.printHint("Запит надіслано. Спостерігайте за його станом.")
			} else {
				this.printErr("Сталась помилка. Сервер відхилив запит.")
			}
		},

		printHint(msg) {
			this.executionResultMessage = msg
			this.executionOk = true
			this.autoCleanPromt()
		},

		printErr(msg) {
			this.executionResultMessage = msg
			this.executionOk = false
			this.autoCleanPromt()
		},

		autoCleanPromt() {
			if (this.cleanTimeout !== null) {
				clearTimeout(this.cleanTimeout)
				this.cleanTimeout = null
			}

			this.cleanTimeout = setTimeout(
				() => {
					this.executionResultMessage = ""
					this.executionOk = null
				},
				this.$options.autocleanPromtTimeout
			);
		},

		autoBlockForm() {
			if (this.blockFormTimeout !== null) {
				clearTimeout(this.blockFormTimeout)
				this.blockFormTimeout = null
			}

			this.blockFormTimeout = setTimeout(
				() => {
					clearTimeout(this.blockFormTimeout)
					this.blockFormTimeout = null
				},
				this.$options.afterSubmitBlockTimeout
			);
		},
	},

	computed: {
		station() { return this.stationsStore.station(this.stationId) },
		
		monthFirstDayDate () {
			const date = new Date();
			const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
			return this.formatDate(firstDay)
		},

		monthLastDayDate () {
			const date = new Date();
			const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
			return this.formatDate(lastDay)
		},

		isFormEnabled() { return this.accountStore.permissions.includes('make_reports') },		
	},
}
</script>


<style src="@/assets/stationForm.css" scoped>
</style>
